/** Human addresses remain ordinary globally unique namekeys. @ separates
 * components; the leading display @ is not stored. No graph lookup is needed. */
export const MAX_NAMEKEY_LENGTH = 85

export type NameScopeStatus = 'valid' | 'missing-parent' | 'sign-in-required' | 'forbidden' | 'invalid-name'
export interface NameScopeResult {
  status: NameScopeStatus
  scope: string
  missingParent?: string
}

export function nameScopeMessage(result: NameScopeResult | { status: 'checking' | 'error'; scope: string }): string {
  switch (result.status) {
    case 'valid': return `This msg is inside @${result.scope}.`
    case 'checking': return 'Checking parent…'
    case 'missing-parent': return `Parent @${result.missingParent || result.scope} does not exist.`
    case 'sign-in-required': return 'Sign in with a Message to create here.'
    case 'forbidden': return `You need edit access to @${result.scope}. Unlock it with its edit PIN.`
    case 'invalid-name': return 'Enter a valid parent name.'
    case 'error': return 'Could not check the parent. Try again.'
  }
}

export function normalizeNameKey(value: string): string {
  return value.split('@').map(part => part
    .replace(/[^\p{L}\p{N}.]+/gu, '')
    .replace(/(?<!^answer)[.]+/giu, '')
    .toLowerCase()).join('@')
}

export function validNameKey(value: string): boolean {
  return value.length <= MAX_NAMEKEY_LENGTH
    && value.split('@').every(part => {
      const normalized = normalizeNameKey(part)
      return normalized.length > 0 && !/^answer\./i.test(normalized)
    })
}

export function splitNameKey(value: string): { scope: string; local: string } {
  const index = value.lastIndexOf('@')
  return index < 0 ? { scope: '', local: value } : { scope: value.slice(0, index), local: value.slice(index + 1) }
}

export function scopedNameKey(scope: string, local: string): string | undefined {
  // A local component cannot smuggle in another namespace.
  if (local.includes('@') || (scope && !validNameKey(scope))) return undefined
  const full = scope ? `${scope}@${local}` : local
  return validNameKey(full) ? full : undefined
}
